import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fileApi } from "~/api";
import type { Item } from "~/types/api";

const getTaskItemsQueryKey = (taskId: number) =>
  fileApi.queryOptions("get", "/api/task/{taskId}/item", {
    params: { path: { taskId } },
  }).queryKey;

const fetchTaskItems = async (taskId: number): Promise<Item[]> => {
  const response = await fileApi.client.GET("/api/task/{taskId}/item", {
    params: { path: { taskId } },
  });

  if (response.error) {
    throw response.error;
  }

  return (response.data || []) as Item[];
};

export const useTaskItems = (taskId: number, enabled = true) => {
  const { data, isLoading, error, refetch } = useQuery<Item[]>({
    queryKey: getTaskItemsQueryKey(taskId),
    queryFn: () => fetchTaskItems(taskId),
    meta: { errorTitle: "Failed to load task items" },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    items: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const usePrefetchTaskItems = () => {
  const queryClient = useQueryClient();

  const prefetchItems = async (taskId: number) => {
    await queryClient.prefetchQuery({
      queryKey: getTaskItemsQueryKey(taskId),
      queryFn: () => fetchTaskItems(taskId),
      staleTime: 5 * 60 * 1000,
    });
  };

  return { prefetchItems };
};

export { getTaskItemsQueryKey, fetchTaskItems };
